import { useCallback, useEffect, useState } from 'react';
import { Button, Space, Table, Tag, Typography, message } from 'antd';
import { authFetch } from '../../utils/api';

type RateLimitRow = {
  id: string;
  brand: string;
  scope: 'ip' | 'device' | 'unknown';
  identifier: string | null;
  count: number;
  expiresInSeconds: number;
};

export function MemberAuthRateLimitsPage() {
  const [rows, setRows] = useState<RateLimitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [clearingId, setClearingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await (await authFetch('/api/admin/member-auth-rate-limits/')).json();
      if (result.code === 0) setRows(result.data ?? []);
      else message.error(result.msg || '讀取限流資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const clear = async (row: RateLimitRow) => {
    if (clearingId != null) return;
    setClearingId(row.id);
    try {
      const response = await authFetch(
        `/api/admin/member-auth-rate-limits/${encodeURIComponent(row.id)}`,
        {
          method: 'DELETE',
          headers: { 'X-Target-Brand': row.brand },
        }
      );
      const result = await response.json();
      if (result.code === 0 && result.data?.cleared) {
        message.success('已解除 rate limit');
        await load();
        return;
      }
      message.error(result.msg || '此限流項目已失效或解除失敗');
    } catch {
      message.error('解除 rate limit 失敗，請稍後再試');
    } finally {
      setClearingId(null);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          會員認證限流
        </Typography.Title>
        <Typography.Text type="secondary">
          顯示目前超過 10 次／15 分鐘的認證限流項目。解除後可立即重新登入；下一次請求仍會重新開始計數。
        </Typography.Text>
      </div>
      <Space>
        <Tag color="orange">目前 {rows.length} 個限流項目</Tag>
        <Button onClick={() => void load()} loading={loading}>重新整理</Button>
      </Space>
      <Table<RateLimitRow>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        columns={[
          { title: '品牌', dataIndex: 'brand', width: 130 },
          {
            title: '類型',
            dataIndex: 'scope',
            width: 100,
            render: (scope: RateLimitRow['scope']) => (
              <Tag color={scope === 'device' ? 'blue' : 'purple'}>
                {scope === 'device' ? '裝置' : scope === 'ip' ? 'IP' : '未知'}
              </Tag>
            ),
          },
          {
            title: 'IP／裝置',
            dataIndex: 'identifier',
            render: (value: string | null) => value || '（舊限流資料，未記錄）',
          },
          { title: '嘗試次數', dataIndex: 'count', width: 110 },
          {
            title: '剩餘時間',
            dataIndex: 'expiresInSeconds',
            width: 130,
            render: (seconds: number) => `${Math.ceil(seconds / 60)} 分鐘`,
          },
          {
            title: '操作',
            width: 130,
            render: (_, row) => (
              <Button
                danger
                loading={clearingId === row.id}
                disabled={clearingId != null && clearingId !== row.id}
                onClick={() => void clear(row)}
              >
                解除
              </Button>
            ),
          },
        ]}
      />
    </Space>
  );
}
